<template>
  <BittsModal
    :visible="visible"
    :title="`Share ${reportIds.length} ${reportIds.length === 1 ? 'list' : 'lists'}`"
    :action-loading="isSubmitting"
    primary-button-text="Share"
    secondary-button-text="Cancel"
    :width="520"
    variant="data"
    :disabled="!canSubmit"
    @action="onSubmit"
    @closed="onClose"
  >
    <div class="bulk-share-report-modal">
      <div v-if="isLoadingUsers" class="flex items-center justify-center py-24">
        <BittsLoading :is-loading="true" :icon-width="40" />
      </div>
      <template v-else>
        <p class="text-neutral-text-strong font-bold pb-8"> List Access </p>
        <div class="bulk-share-report-modal__list">
          <div
            v-for="(row, index) in pendingRows"
            :key="`row-${index}`"
            class="bulk-share-report-modal__row"
          >
            <BittsSelect
              :aria-label="`User, row ${index + 1}`"
              :model-value="row.userId"
              :options="getAvailableOptionsForRow(index)"
              option-type="entity"
              placeholder="Select user"
              :prefix-icon="['far', 'user']"
              size="medium"
              :filter-function="filterUserOption"
              class="bulk-share-report-modal__user-picker"
              data-testid="bulk-share-user-picker"
              @update:model-value="
                (userId: number) => onUserChange(index, userId)
              "
            >
              <template #notFoundContent>
                <p class="text-neutral-text text-center">
                  {{
                    getAvailableOptionsForRow(index).length === 0
                      ? 'All team members already have access'
                      : 'No results found'
                  }}
                </p>
              </template>
              <template #option="{ option }">
                <div class="flex items-center w-full bulk-share-user-option">
                  <BittsAvatar
                    size="small"
                    :show-initials="true"
                    :force-circle="!option.picture_url"
                    :user="option"
                    class="mr-10"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="text-neutral-text-strong truncate">
                      {{ option.label }}
                    </div>
                    <div
                      class="text-sm text-neutral-text-weak truncate bulk-share-user-option__email"
                    >
                      {{ option.email }}
                    </div>
                  </div>
                </div>
              </template>
            </BittsSelect>
            <ReportRolePicker
              :model-value="row.accessLevel"
              :eligible-permissions="getEligiblePermissionsForUser(row.userId)"
              :disabled="row.userId == null"
              :dropdown-match-select-width="false"
              class="bulk-share-report-modal__role-picker"
              @update:model-value="
                (val: ReportAccessLevel | 'remove_user') =>
                  onRoleChange(index, val)
              "
            />
          </div>
          <component :is="addMoreWrapperComponent" v-bind="addMoreWrapperProps">
            <BittsButton
              type="neutral"
              variant="ghost"
              size="x-small"
              text="Add more people"
              :left-icon="['far', 'plus']"
              :disabled="!canAddMorePeople"
              data-testid="bulk-share-add-more-people"
              @click="onAddMorePeopleClick"
            />
            <template v-if="!canAddMorePeople" #title>
              No one left to add
            </template>
          </component>
        </div>
        <div>
          <BittsDivider class="my-16" />
          <p class="text-neutral-text-strong font-bold pb-8">General Access</p>
          <GeneralAccessSelector
            v-model="generalAccessLevel"
            :show-clear-option="true"
            class="mb-12"
          />
        </div>
      </template>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsDivider,
  BittsLoading,
  BittsModal,
  BittsSelect,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { computed, onMounted, ref } from 'vue';

import GeneralAccessSelector from '@/components/reports/GeneralAccessSelector.vue';
import ReportRolePicker from '@/components/reports/ReportRolePicker.vue';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import { useFlashesStore } from '@/stores';
import {
  BulkTeamAccessAuthorization,
  GeneralAccessLevel,
  ReportAccessLevel,
} from '@/types/report_team_access';
import { getHighestEligiblePermission } from '@/utils/reportPermissions';

const { reportIds = [], visible = false } = defineProps<{
  reportIds: string[];
  visible?: boolean;
}>();

const emit = defineEmits<{
  closed: [];
  shared: [];
}>();

const flashesStore = useFlashesStore();
const { iteratively } = useIteratively();

const generalAccessLevel = ref<GeneralAccessLevel | null>(null);

type PendingRow = {
  userId: number | null;
  accessLevel: ReportAccessLevel | null;
};

const isLoadingUsers = ref(false);
const isSubmitting = ref(false);
const authorizations = ref<BulkTeamAccessAuthorization[]>([]);
const pendingRows = ref<PendingRow[]>([{ userId: null, accessLevel: null }]);

const eligibleAuthorizations = computed(() =>
  authorizations.value.filter((auth) => !auth.has_access),
);

const authByUserId = computed(
  () => new Map(eligibleAuthorizations.value.map((a) => [a.user.id, a])),
);

function buildOption(auth: BulkTeamAccessAuthorization) {
  return {
    value: auth.user.id,
    label: `${auth.user.first_name} ${auth.user.last_name}`,
    first_name: auth.user.first_name,
    last_name: auth.user.last_name,
    email: auth.user.email,
    picture_url: auth.user.picture_url,
  };
}

type UserOption = ReturnType<typeof buildOption>;

function filterUserOption(
  val: string,
  option: { value: number },
  options: UserOption[],
) {
  const found = options.find((opt) => opt.value === option.value);
  if (!found) return false;
  const searchTerm = val.trim().toLowerCase();
  return (
    found.label.toLowerCase().includes(searchTerm) ||
    found.email.toLowerCase().includes(searchTerm)
  );
}

function getAvailableOptionsForRow(rowIndex: number) {
  const pickedElsewhere = new Set(
    pendingRows.value
      .filter((_, i) => i !== rowIndex)
      .map((r) => r.userId)
      .filter((id) => id != null),
  );
  return eligibleAuthorizations.value
    .filter((auth) => !pickedElsewhere.has(auth.user.id))
    .map(buildOption);
}

function getEligiblePermissionsForUser(
  userId: number | null,
): ReportAccessLevel[] {
  if (userId == null) return [];
  return (authByUserId.value.get(userId)?.eligible_report_permissions ??
    []) as ReportAccessLevel[];
}

function onUserChange(rowIndex: number, userId: number) {
  const auth = authByUserId.value.get(userId);
  if (!auth) return;
  const list = [...pendingRows.value];
  list[rowIndex] = {
    userId,
    accessLevel: getHighestEligiblePermission(auth.eligible_report_permissions),
  };
  pendingRows.value = list;
}

function onRoleChange(
  rowIndex: number,
  value: ReportAccessLevel | 'remove_user',
) {
  if (value === 'remove_user') {
    const next = pendingRows.value.filter((_, i) => i !== rowIndex);
    pendingRows.value =
      next.length > 0 ? next : [{ userId: null, accessLevel: null }];
    return;
  }
  const list = [...pendingRows.value];
  if (list[rowIndex]) {
    list[rowIndex] = { ...list[rowIndex], accessLevel: value };
    pendingRows.value = list;
  }
}

const canAddMorePeople = computed(() => {
  if (pendingRows.value.some((r) => r.userId == null)) return false;
  const pickedIds = new Set(pendingRows.value.map((r) => r.userId));
  return eligibleAuthorizations.value.some(
    (auth) => !pickedIds.has(auth.user.id),
  );
});

const addMoreWrapperComponent = computed(() =>
  canAddMorePeople.value ? 'div' : BittsTooltip,
);

const addMoreWrapperProps = computed(() =>
  canAddMorePeople.value
    ? {}
    : { trigger: 'hover', placement: 'top', overlayClass: 'w-max' },
);

function onAddMorePeopleClick() {
  const pickedIds = new Set(
    pendingRows.value.map((r) => r.userId).filter((id) => id != null),
  );
  const firstAvailable = eligibleAuthorizations.value.find(
    (auth) => !pickedIds.has(auth.user.id),
  );
  if (!firstAvailable) return;
  pendingRows.value.push({
    userId: firstAvailable.user.id,
    accessLevel: getHighestEligiblePermission(
      firstAvailable.eligible_report_permissions,
    ),
  });
}

const canSubmit = computed(
  () =>
    pendingRows.value.some((r) => r.userId != null && r.accessLevel != null) ||
    generalAccessLevel.value !== null,
);

async function fetchEligibleUsers() {
  isLoadingUsers.value = true;
  try {
    const { data, error } = await crossbeamApi.POST(
      '/v0.1/reports/bulk-team-access',
      { body: { report_ids: reportIds } },
    );
    if (error) throw error;
    authorizations.value = data?.authorizations ?? [];
    if (eligibleAuthorizations.value.length === 1) {
      const auth = eligibleAuthorizations.value[0];
      if (auth) {
        pendingRows.value = [
          {
            userId: auth.user.id,
            accessLevel: getHighestEligiblePermission(
              auth.eligible_report_permissions,
            ),
          },
        ];
      }
    }
  } catch {
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'Could not load eligible users',
    });
    emit('closed');
  } finally {
    isLoadingUsers.value = false;
  }
}

async function onSubmit() {
  isSubmitting.value = true;

  try {
    const userAccess = pendingRows.value
      .filter(
        (r): r is { userId: number; accessLevel: ReportAccessLevel } =>
          r.userId != null && r.accessLevel != null,
      )
      .map((r) => ({ user_id: r.userId, access_level: r.accessLevel }));

    const accessLevelToSave = generalAccessLevel.value;
    const shouldSaveUserAccess = userAccess.length > 0;

    const [userAccessResult, generalAccessResult] = await Promise.all([
      shouldSaveUserAccess
        ? crossbeamApi.PUT('/v0.1/reports/bulk-user-access', {
            body: { report_ids: reportIds, user_access: userAccess },
          })
        : null,
      accessLevelToSave !== null
        ? crossbeamApi.PUT('/v0.1/reports/general-access', {
            body: { report_ids: reportIds, access_level: accessLevelToSave },
          })
        : null,
    ]);

    if (
      userAccessResult?.error !== undefined ||
      generalAccessResult?.error !== undefined
    ) {
      flashesStore.addErrorFlash({
        message: 'Something went wrong',
        description: 'Lists not shared',
      });
      return;
    }

    if (accessLevelToSave !== null) {
      reportIds.forEach((reportId) => {
        iteratively.userSharesListAction({
          action: 'changed_general_access',
          report_id: reportId,
          event_site: EVENT_SITES.REPORTS_LIST_VIEW,
          general_access: accessLevelToSave,
        } as never);
      });
    }

    flashesStore.addSuccessFlash({
      message: `${reportIds.length} ${reportIds.length === 1 ? 'list' : 'lists'} shared successfully`,
    });
    emit('shared');
    emit('closed');
  } finally {
    isSubmitting.value = false;
  }
}

function onClose() {
  emit('closed');
}

onMounted(fetchEligibleUsers);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.bulk-share-report-modal {
  @apply pt-16 pb-8 flex flex-col;
}

.bulk-share-report-modal__list {
  @apply max-h-[350px] overflow-y-auto;
}

.bulk-share-report-modal__row {
  @apply flex items-start gap-8 mb-8;
}

.bulk-share-report-modal__user-picker {
  @apply flex-1;
}

.bulk-share-report-modal__role-picker {
  @apply w-120 shrink-0;
}

.bulk-share-report-modal
  :deep(.ant-select-selection-item .bulk-share-user-option__email) {
  @apply hidden;
}

.bulk-share-report-modal
  :deep(.ant-select-selection-item .bulk-share-user-option) {
  .bitts-avatar.mr-10 {
    @apply mr-8;
  }
}

:deep(.o-bitts-modal__content) {
  @apply mx-0 px-24;
}
</style>
